import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  UserPlus,
  BedDouble,
  FileText,
  FlaskConical,
  Scan,
  Pill,
  Package,
  Receipt,
  Settings,
  LogOut,
  Stethoscope,
  CalendarDays,
  MessageSquare,
  ClipboardCheck,
  CalendarOff,
  Workflow,
  TestTube,
  DoorOpen,
  User,
  Search,
  Building2,
  Globe,
  CreditCard,
  BarChart3,
  Bell,
  Brain,
  Shield,
  GitPullRequest,
  Webhook,
  Database,
  MessageCircle,
  Headset,
  Lightbulb,
} from 'lucide-react';

const Sidebar = () => {
  const { user, institution, logout, hasRole, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Platform admin specific navigation
  const platformNavItems = [
    {
      section: 'Platform',
      items: [
        { icon: Globe, label: 'Platform Dashboard', path: '/platform', roles: ['super_admin'] },
        { icon: BarChart3, label: 'Platform Analytics', path: '/platform/analytics', roles: ['super_admin'] },
        { icon: Brain, label: 'ML Insights', path: '/platform/ml-insights', roles: ['super_admin'] },
        { icon: Bell, label: 'Alert Thresholds', path: '/platform/alert-thresholds', roles: ['super_admin'] },
        { icon: Shield, label: 'Roles & Departments', path: '/platform/roles-departments', roles: ['super_admin'] },
        { icon: Database, label: 'Master Data', path: '/platform/master-data', roles: ['super_admin'] },
        { icon: Lightbulb, label: 'Suggested Items', path: '/platform/suggested-items', roles: ['super_admin'] },
        { icon: GitPullRequest, label: 'Pending Approvals', path: '/platform/pending-contributions', roles: ['super_admin'] },
        { icon: Headset, label: 'Support Inbox', path: '/support/inbox', roles: ['super_admin'] },
      ],
    },
  ];

  const navItems = [
    {
      section: 'Overview',
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', roles: ['super_admin', 'admin', 'doctor', 'nurse', 'receptionist', 'lab_technician', 'pharmacist', 'stockist'] },
      ],
    },
    {
      section: 'Patient Management',
      items: [
        { icon: UserPlus, label: 'Register Patient', path: '/patients/register', roles: ['super_admin', 'admin', 'receptionist'] },
        { icon: Search, label: 'Patient Search', path: '/patients/search', roles: ['super_admin', 'admin', 'receptionist'] },
        { icon: Users, label: 'All Patients', path: '/patients', roles: ['super_admin', 'admin', 'doctor', 'nurse', 'receptionist'] },
        { icon: BedDouble, label: 'Admissions', path: '/admissions', roles: ['super_admin', 'admin', 'doctor', 'nurse', 'receptionist'] },
      ],
    },
    {
      section: 'Clinical',
      items: [
        { icon: CalendarDays, label: 'Appointments', path: '/appointments', roles: ['super_admin', 'admin', 'doctor', 'nurse', 'receptionist'] },
        { icon: FileText, label: 'Prescriptions', path: '/prescriptions', roles: ['super_admin', 'admin', 'doctor', 'nurse', 'pharmacist'] },
        { icon: Stethoscope, label: 'Procedures', path: '/procedures', roles: ['super_admin', 'admin', 'doctor', 'nurse'] },
      ],
    },
    {
      section: 'Diagnostics',
      items: [
        { icon: FlaskConical, label: 'Lab Tests', path: '/lab-tests', roles: ['super_admin', 'admin', 'doctor', 'nurse', 'lab_technician'] },
        { icon: Scan, label: 'Imaging Tests', path: '/imaging-tests', roles: ['super_admin', 'admin', 'doctor', 'nurse', 'lab_technician'] },
      ],
    },
    {
      section: 'Pharmacy',
      items: [
        { icon: Pill, label: 'Dispensing', path: '/pharmacy', roles: ['super_admin', 'admin', 'pharmacist'] },
        { icon: Package, label: 'Stock Management', path: '/stock', roles: ['super_admin', 'admin', 'pharmacist', 'stockist'] },
      ],
    },
    {
      section: 'Finance',
      items: [
        { icon: Receipt, label: 'Billing', path: '/billing', roles: ['super_admin', 'admin', 'receptionist'] },
        { icon: FileText, label: 'Reports', path: '/reports', roles: ['super_admin', 'admin', 'doctor', 'receptionist'] },
      ],
    },
    {
      section: 'Communication',
      items: [
        { icon: MessageSquare, label: 'Case Messages', path: '/messages', roles: ['super_admin', 'admin', 'doctor', 'nurse', 'lab_technician', 'pharmacist'] },
      ],
    },
    {
      section: 'Workflow & Queues',
      items: [
        { icon: Workflow, label: 'Pharmacy Queue', path: '/workflow/pharmacy-queue', roles: ['super_admin', 'admin', 'pharmacist'] },
        { icon: TestTube, label: 'Lab Queue', path: '/workflow/lab-queue', roles: ['super_admin', 'admin', 'lab_technician'] },
        { icon: Scan, label: 'Imaging Queue', path: '/workflow/imaging-queue', roles: ['super_admin', 'admin', 'imaging_technician'] },
        { icon: DoorOpen, label: 'Case Closure', path: '/workflow/reception-closure', roles: ['super_admin', 'admin', 'receptionist'] },
      ],
    },
    {
      section: 'Approvals',
      items: [
        { icon: ClipboardCheck, label: 'Medication Approvals', path: '/medication-approvals', roles: ['super_admin', 'admin', 'doctor', 'nurse'] },
        { icon: CalendarOff, label: 'Leave Management', path: '/leave', roles: ['super_admin', 'admin', 'doctor', 'nurse', 'receptionist', 'lab_technician', 'pharmacist', 'stockist'] },
      ],
    },
    {
      section: 'Administration',
      items: [
        { icon: Settings, label: 'User Management', path: '/users', roles: ['super_admin', 'admin'] },
        { icon: Building2, label: 'Institution Settings', path: '/settings/institution', roles: ['super_admin', 'admin'] },
        { icon: CreditCard, label: 'Subscription', path: '/subscription', roles: ['super_admin', 'admin'] },
        { icon: Receipt, label: 'Pricing Master', path: '/settings/pricing-master', roles: ['super_admin', 'admin', 'receptionist'] },
        { icon: Users, label: 'Referee Management', path: '/settings/referees', roles: ['super_admin', 'admin'] },
        { icon: Webhook, label: 'Webhooks', path: '/settings/webhooks', roles: ['super_admin', 'admin'] },
        { icon: User, label: 'My Settings', path: '/settings/profile', roles: ['super_admin', 'admin', 'doctor', 'nurse', 'receptionist', 'lab_technician', 'pharmacist', 'stockist'] },
      ],
    },
  ];

  const filteredNavItems = navItems.map(section => ({
    ...section,
    items: section.items.filter(item => item.roles.some(role => hasRole(role))),
  })).filter(section => section.items.length > 0);

  // Add platform nav items for platform admins only
  const platformItems = isPlatformAdmin && isPlatformAdmin() ? platformNavItems.map(section => ({
    ...section,
    items: section.items.filter(item => item.roles.some(role => hasRole(role))),
  })).filter(section => section.items.length > 0) : [];

  // If platform admin, ONLY show platform items (no institutional items)
  const allNavItems = isPlatformAdmin && isPlatformAdmin() ? platformItems : [...platformItems, ...filteredNavItems];

  return (
    <aside className="sidebar" data-testid="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <img 
            src="/healventure-logo.png" 
            alt="HMC Buddy" 
            className="w-10 h-10 object-contain"
          />
          <div>
            <h1 className="font-semibold text-slate-900 text-lg font-[Manrope]">HMC Buddy</h1>
            <p className="text-xs text-slate-500">
              {isPlatformAdmin && isPlatformAdmin() ? 'Platform Admin' : 'Hospital Management'}
            </p>
          </div>
        </div>
        
        {/* Institution Name Banner - Show for institutional users only */}
        {institution && institution.institution_name && !isPlatformAdmin() && (
          <div className="mt-3 px-3 py-2 bg-gradient-to-r from-red-50 to-rose-50 border border-red-100 rounded-lg">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-red-600 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-slate-500 font-medium">Institution</p>
                <p className="text-sm font-semibold text-red-600 truncate" title={institution.institution_name}>
                  {institution.institution_name}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <nav className="sidebar-nav">
        {allNavItems.map((section) => (
          <div key={section.section} className="sidebar-section">
            <p className="sidebar-section-title">{section.section}</p>
            <div className="space-y-1">
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? 'active' : ''}`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
            <span className="text-red-600 font-semibold">
              {user?.full_name?.charAt(0) || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.full_name}</p>
            <p className="text-xs text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-slate-600 hover:text-red-600 text-sm w-full px-2 py-2 rounded-lg hover:bg-red-50 transition-colors"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
